import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import mainVideo from "../../static/media/Salon/main.mp4";

const PRIMARY = "#06BBC7";
const DARK_PRIMARY = "#047b84";
const YELLOW = "#F7C948";

const services = [
  "Corte de cabello para damas",
  "Tratamientos capilares",
  "Hidratación profunda capilar",
  "Cauterización capilar",
  "Peeling capilar",
  "Alisado japonés",
  "Tinturados",
  "Cepillados",
  "Planchados",
  "Ondas",
];

const MotionBox = motion(Box);

const Intro = () => {
  const whatsappMessage = encodeURIComponent(
    "Hola Lily's Sunflower, quiero información sobre los servicios de peluquería."
  );

  const whatsappUrl = `https://wa.me/593984781610?text=${whatsappMessage}`;

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 6},
        bgcolor: "#ffffff",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 0.78fr",
            },
            gap: { xs: 4, md: 4.5 },
            alignItems: "center",
          }}
        >
          <MotionBox
            initial={{ opacity: 0, x: -26 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            sx={{
              order: { xs: 2, md: 1 },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "2.25rem", sm: "2.8rem", md: "3.5rem" },
                fontWeight: 500,
                color: "#1d2530",
                letterSpacing: "-0.04em",
                lineHeight: 1.04,
                mb: 2,
              }}
            >
              Peluquería y cuidado capilar
            </Typography>

            <Typography
              sx={{
                maxWidth: 610,
                mx: { xs: "auto", md: 0 },
                color: "#374151",
                fontSize: { xs: "0.98rem", md: "1.1rem" },
                lineHeight: 1.75,
                fontWeight: 400,
                mb: 3,
              }}
            >
              Realza tu estilo con servicios profesionales pensados para cuidar,
              renovar y embellecer tu cabello con atención personalizada.
            </Typography>

            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.3,
                mb: 3.5,
                maxWidth: 670,
              }}
            >
              {services.map((service, index) => (
                <MotionBox
                  key={service}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.42,
                    delay: index * 0.04,
                    ease: "easeOut",
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.1,
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(6, 187, 199, 0.14)",
                    borderRadius: "16px",
                    px: 1.6,
                    py: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      bgcolor: index % 2 === 0 ? PRIMARY : YELLOW,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#1f2937",
                      fontSize: "0.96rem",
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {service}
                  </Typography>
                </MotionBox>
              ))}
            </Box>

            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 0.8,
                mb: 3.5,
                maxWidth: 360,
                mx: "auto",
              }}
            >
              {services.map((service, index) => (
                <MotionBox
                  key={service}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.35,
                    delay: index * 0.035,
                    ease: "easeOut",
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.8,
                    bgcolor:
                      index % 2 === 0
                        ? "rgba(6, 187, 199, 0.08)"
                        : "rgba(247, 201, 72, 0.16)",
                    border: `1px solid ${
                      index % 2 === 0
                        ? "rgba(6, 187, 199, 0.18)"
                        : "rgba(247, 201, 72, 0.36)"
                    }`,
                    borderRadius: "999px",
                    px: 1.25,
                    py: 0.75,
                    maxWidth: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: index % 2 === 0 ? PRIMARY : YELLOW,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#1f2937",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {service}
                  </Typography>
                </MotionBox>
              ))}
            </Box>

            <Button
              component="a"
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              endIcon={<WhatsAppIcon />}
              sx={{
                bgcolor: YELLOW,
                color: "#1f2937",
                px: { xs: 3.2, md: 4 },
                py: 1.2,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: { xs: "0.95rem", md: "1rem" },
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#efbd35",
                  boxShadow: "none",
                },
              }}
            >
              Escríbenos
            </Button>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, x: 26, scale: 0.98 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            sx={{
              order: { xs: 1, md: 2 },
              position: "relative",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: { xs: 78, sm: 105, md: 118 },
                height: { xs: 78, sm: 105, md: 118 },
                borderRadius: "50%",
                bgcolor: YELLOW,
                opacity: 0.75,
                right: { xs: "13%", sm: "24%", md: 18 },
                bottom: { xs: -12, md: -14 },
              }}
            />

            <Box
              sx={{
                position: "absolute",
                width: { xs: 90, sm: 125, md: 145 },
                height: { xs: 90, sm: 125, md: 145 },
                borderRadius: "50%",
                bgcolor: "rgba(6, 187, 199, 0.1)",
                left: { xs: "13%", sm: "24%", md: 18 },
                top: { xs: -12, md: -16 },
              }}
            />

            <Box
              sx={{
                position: "relative",
                width: { xs: "min(72vw, 250px)", sm: 300, md: 330 },
                borderRadius: { xs: "24px", md: "32px" },
                overflow: "hidden",
                border: "1px solid rgba(4, 123, 132, 0.16)",
                bgcolor: DARK_PRIMARY,
              }}
            >
              <Box
                component="video"
                src={mainVideo}
                autoPlay
                muted
                loop
                playsInline
                controls
                sx={{
                  width: "100%",
                  height: { xs: 420, sm: 510, md: 585 },
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(4, 123, 132, 0.02) 0%, rgba(4, 123, 132, 0.12) 100%)",
                  pointerEvents: "none",
                }}
              />
            </Box>
          </MotionBox>
        </Box>
      </Container>
    </Box>
  );
};

export default Intro;